import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form } from "antd";
import * as React from "react";
import { ZSpecCommon } from "src/business/specification";
import { getWorkflowStepDisplayName } from "src/business/workflow/getWorkflowStepDisplayName";
import { ZWorkflow } from "src/business/workflow/ZWorkflow";
import { ZWorkflowStep } from "src/business/workflow/ZWorkflowStep";
import { classNames } from "src/common/classNames";
import { DeleteConfirm } from "src/components/DeleteConfirm";
import {
  makeMdmActionColumn,
  MdmTable,
  RenderButtonAction,
} from "src/components/MdmTable";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { ToolBox2 } from "src/components/ToolBox2";

import { PrevStepsCell } from "./PrevStepsCell";
import * as styles from "./WorkflowCardStepsTab.module.less";
import { WorkflowStepModal } from "./WorkflowStepModal";

type PropsWorkflowCardStepsTab = {
  hidden: boolean;
  specs: ZSpecCommon[];
  onChange: () => void;
};

const keySteps: keyof ZWorkflow = "steps";

export const WorkflowCardStepsTab: React.FC<PropsWorkflowCardStepsTab> = (
  props,
) => {
  const { hidden, specs, onChange } = props;
  const steps: ZWorkflowStep[] = Form.useWatch(keySteps) ?? [];
  const form = Form.useFormInstance();
  const stepsTable = React.useMemo(
    () => createWorkflowStepsTable(steps),
    [steps],
  );

  const deleteById = (targetId: string) => {
    const newSteps = steps
      .filter(({ id }) => id !== targetId)
      .map((step) => ({
        ...step,
        predecessors: step.predecessors?.filter(({ id }) => id !== targetId),
      }));
    form.setFieldValue(keySteps, newSteps);
    onChange();
  };
  const [delStep, setDelStep] = React.useState<ZWorkflowStep | undefined>();
  const [activeStep, setActiveStep] = React.useState<
    ZWorkflowStep | undefined
  >();
  const onNewStep = () => {
    setActiveStep({
      id: `@${Math.random()}`, // Это нужно для правильной работы таблицы, но при отправке запроса необходимо удалять
      name: "",
      displayName: "",
      roles: [],
      attributes: [],
      lastStep: false,
    });
    onChange();
  };
  const onStepChange = (step: ZWorkflowStep) => {
    const newSteps = [...steps];
    const pos = newSteps.findIndex(({ id }) => id === step.id);
    if (pos < 0) {
      newSteps.push(step);
    } else {
      newSteps[pos] = step;
    }
    form.setFieldValue(keySteps, newSteps);
    onChange();
  };
  const columns = React.useMemo<RtColumn<ZWorkflowStep>[]>(
    () => [
      {
        key: "name",
        title: "Системное название",
        width: "15%",
        render: (row) => (
          <Button
            type="link"
            onClick={() => setActiveStep(row)}
            style={{ padding: 0 }}
          >
            {row.name}
          </Button>
        ),
      },
      {
        key: "displayName",
        title: "Отображаемое название",
        width: "15%",
        render: (step) => getWorkflowStepDisplayName(step),
      },
      {
        key: "description",
        title: "Описание",
        render: ({ description }) => description,
      },
      {
        key: "prevSteps",
        title: "Предшественник",
        width: 200,
        render: (step) => <PrevStepsCell curStep={step} allSteps={steps} />,
      },
      makeMdmActionColumn<ZWorkflowStep>({
        key: "delete",
        render: (step) => (
          <RenderButtonAction
            icon={<DeleteOutlined />}
            title={`Удалить ${getWorkflowStepDisplayName(step)}`}
            onClick={() => setDelStep(step)}
          />
        ),
      }),
    ],
    [steps],
  );

  return (
    <div className={classNames([styles.tabContent, [hidden, styles.hidden]])}>
      <ToolBox2
        mainTool={<div className={styles.title}>Шаги</div>}
        extraTools={
          <Button icon={<PlusOutlined />} onClick={onNewStep}>
            Добавить шаг
          </Button>
        }
      >
        <Form.Item name={keySteps} className={styles.stepsItem}>
          <div className={styles.tableBox}>
            {!hidden && (
              <MdmTable
                store={stepsTable}
                columns={columns}
                resizableColumns
                verticalMode="fit"
                components={{ Footer: () => null }}
              />
            )}
          </div>
        </Form.Item>
        <WorkflowStepModal
          activeStep={activeStep}
          close={() => setActiveStep(undefined)}
          allSteps={steps}
          specs={specs}
          onApply={onStepChange}
        />
        <DeleteConfirm
          open={!!delStep}
          close={() => setDelStep(undefined)}
          content={`Шаг «${getWorkflowStepDisplayName(delStep)}» будет удалён без возможности восстановления. Переходы к данному шагу будут удалены автоматически`}
          task={async () => {
            if (delStep) deleteById(delStep.id);
          }}
        />
      </ToolBox2>
    </div>
  );
};

const createWorkflowStepsTable = (rows: ZWorkflowStep[]) =>
  new RtStore<ZWorkflowStep, {}>({
    rowKey: "id",
    loader: async () => ({
      rows,
      totalRows: rows.length,
    }),
  });
